import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('laksh_token'));
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const fetchUser = useCallback(async (authToken) => {
    if (!authToken) {
      setLoading(false);
      setInitialized(true);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data.user);
      setBusiness(response.data.business);
    } catch (error) {
      console.error('Auth error:', error);
      // Only logout on actual auth errors, not network errors
      if (error.response?.status === 401) {
        localStorage.removeItem('laksh_token');
        setToken(null);
        setUser(null);
        setBusiness(null);
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (!initialized) {
      fetchUser(token);
    }
  }, [token, initialized, fetchUser]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData, business: businessData } = response.data;
    localStorage.setItem('laksh_token', newToken);
    setToken(newToken);
    setUser(userData);
    setBusiness(businessData);
    return response.data;
  }, []);

  const signup = useCallback(async (data) => {
    const response = await axios.post(`${API}/auth/signup`, data);
    const { token: newToken, user: userData, business: businessData } = response.data;
    localStorage.setItem('laksh_token', newToken);
    setToken(newToken);
    setUser(userData);
    setBusiness(businessData);
    return response.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('laksh_token');
    setToken(null);
    setUser(null);
    setBusiness(null);
  }, []);

  const updateBusiness = useCallback((data) => {
    setBusiness(prev => ({ ...prev, ...data }));
  }, []);

  const value = useMemo(() => ({
    user,
    business,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    signup,
    logout,
    updateBusiness,
  }), [user, business, token, loading, login, signup, logout, updateBusiness]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// API client with auth
export const createApiClient = (token) => {
  const client = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return client;
};

export const useApi = () => {
  const { token } = useAuth();
  return useMemo(() => createApiClient(token), [token]);
};
